import {
  MutateVariablePayload,
  VariableDataResult,
  VariableDto,
  VariableSourceSettingsDto,
  VariableType,
} from '@thingslog/repositories';
import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { QueryKeys } from '@thingslog/queries';
import { useQuery } from '@tanstack/react-query';
import React, { FC, useEffect, useMemo } from 'react';
import { Controller, FieldValues, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { VariableSourceSettingsFactory } from '../../../factories/VariableSourceSettings.factory';

interface VariableFormBaseProps {
  type: 'create' | 'edit';
  variableSources: VariableSourceSettingsDto[];
  companyId?: number | null;
  disabled?: boolean;
  onCancel: () => void;
}

interface VariableFormCreateProps extends VariableFormBaseProps {
  type: 'create';
  onSubmit: (payload: MutateVariablePayload) => void;
}

interface VariableFormEditProps extends VariableFormBaseProps {
  type: 'edit';
  defaultValues: VariableDto;
  onSubmit: (id: string, payload: MutateVariablePayload) => void;
}

type Props = VariableFormCreateProps | VariableFormEditProps;

interface VariableFormValues {
  type: VariableType;
  name: string;
  label: string;
  variableSourceId: string | null;
  value: string | string[];
}

interface VariableSourceOption {
  name: string;
  value: string;
}

export const VariableForm: FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const editDefaultValues = props.type === 'edit' ? props.defaultValues : null;
  const defaultVariableSource = useMemo(() => {
    return props.variableSources.find(
      (source: VariableSourceSettingsDto): boolean => source.source.type === 'device-numbers'
    );
  }, [props.variableSources]);

  const defaultValues = useMemo<VariableFormValues>(() => {
    if (editDefaultValues) {
      return {
        type: editDefaultValues.type,
        name: editDefaultValues.name,
        label: editDefaultValues.label,
        variableSourceId: editDefaultValues.variableSource?.id || null,
        value: editDefaultValues.value,
      };
    }

    return {
      type: 'single-value',
      name: '',
      label: '',
      variableSourceId: defaultVariableSource?.id || null,
      value: '',
    };
  }, [editDefaultValues, defaultVariableSource]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<VariableFormValues>({
    mode: 'onChange',
    defaultValues,
  });

  const variableType = useWatch({ control, name: 'type' });
  const variableSourceId = useWatch({ control, name: 'variableSourceId' });

  const selectedVariableSource = useMemo(() => {
    return props.variableSources.find(
      (source: VariableSourceSettingsDto) => source.id === variableSourceId
    );
  }, [props.variableSources, variableSourceId]);

  const sourceOptionsQuery = useQuery(
    [QueryKeys.GetVariableSources, 'options', selectedVariableSource?.id, props.companyId],
    async (): Promise<VariableSourceOption[]> => {
      if (!selectedVariableSource) {
        return [];
      }

      const result: VariableDataResult | null = await VariableSourceSettingsFactory.execute(
        selectedVariableSource.source.type,
        props.companyId ?? null
      );

      return result?.result ?? [];
    },
    {
      enabled: !!selectedVariableSource,
      refetchOnWindowFocus: false,
    }
  );

  const sourceOptions = sourceOptionsQuery.data ?? [];
  const hasVariableSource = !!selectedVariableSource;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleTypeChange = (type: VariableType): void => {
    setValue('type', type, { shouldDirty: true, shouldValidate: true });
    setValue('value', type === 'single-value' ? '' : [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleSourceChange = (variableSourceId: string | null): void => {
    setValue('variableSourceId', variableSourceId, { shouldDirty: true, shouldValidate: true });
    setValue('value', variableType === 'single-value' ? '' : [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleFormSubmit = (values: VariableFormValues): void => {
    if (values.type === 'single-value') {
      const mutatePayload: MutateVariablePayload = {
        type: 'single-value',
        name: values.name,
        label: values.label,
        variableSourceId: values.variableSourceId,
        value: Array.isArray(values.value) ? values.value[0] ?? '' : values.value,
      };
      if (props.type === 'edit') {
        props.onSubmit(props.defaultValues.id, mutatePayload);
      } else {
        props.onSubmit(mutatePayload);
      }
      return;
    }

    const value = Array.isArray(values.value)
      ? values.value
      : values.value
          .split(',')
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0);

    const mutatePayload: MutateVariablePayload = {
      type: 'multi-value',
      name: values.name,
      label: values.label,
      variableSourceId: values.variableSourceId,
      value,
    };
    if (props.type === 'edit') {
      props.onSubmit(props.defaultValues.id, mutatePayload);
    } else {
      props.onSubmit(mutatePayload);
    }
  };

  return (
    <form className="flex flex-col gap-4 p-4" onSubmit={handleSubmit(handleFormSubmit)}>
      <div
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        data-dashboard-tour="variable-form-name-label"
      >
        <Controller
          name="name"
          control={control}
          rules={{
            required: t('dashboard_variable_name_required', 'Name is required'),
            pattern: {
              value: /^[a-zA-Z_][a-zA-Z0-9_]*$/,
              message: t(
                'dashboard_variable_name_pattern',
                'Use letters, numbers, and underscores. Start with a letter or underscore.'
              ),
            },
          }}
          render={({ field }: FieldValues): JSX.Element => (
            <TextField
              {...field}
              label={t('dashboard_variable_name', 'Name')}
              size="small"
              fullWidth
              disabled={props.disabled}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
          )}
        />

        <Controller
          name="label"
          control={control}
          rules={{ required: t('dashboard_variable_label_required', 'Label is required') }}
          render={({ field }: FieldValues): JSX.Element => (
            <TextField
              {...field}
              label={t('dashboard_variable_label', 'Label')}
              size="small"
              fullWidth
              disabled={props.disabled}
              error={!!errors.label}
              helperText={errors.label?.message}
            />
          )}
        />
      </div>

      <div data-dashboard-tour="variable-form-type">
        <Controller
          name="type"
          control={control}
          render={({ field }: FieldValues): JSX.Element => (
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={field.value}
              disabled={props.disabled}
              onChange={(_: React.MouseEvent<HTMLElement>, value: VariableType | null): void => {
                if (value) {
                  handleTypeChange(value);
                }
              }}
            >
              <ToggleButton value="single-value">
                {t('dashboard_variable_single', 'Single')}
              </ToggleButton>
              <ToggleButton value="multi-value">
                {t('dashboard_variable_multi', 'Multi')}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        />
      </div>

      <div data-dashboard-tour="variable-form-source">
        <Controller
          name="variableSourceId"
          control={control}
          render={({ field }: FieldValues): JSX.Element => (
            <FormControl size="small" fullWidth>
              <InputLabel>{t('dashboard_data_source', 'Data Source')}</InputLabel>
              <Select
                {...field}
                label={t('dashboard_data_source', 'Data Source')}
                value={field.value ?? ''}
                disabled={props.disabled}
                onChange={(event: SelectChangeEvent<string>): void => {
                  handleSourceChange(event.target.value || null);
                }}
              >
                <MenuItem value="">
                  <em>{t('none', 'None')}</em>
                </MenuItem>
                {props.variableSources.map((source: VariableSourceSettingsDto) => (
                  <MenuItem key={source.id} value={source.id}>
                    {source.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </div>

      <div data-dashboard-tour="variable-form-value">
        <Controller
          name="value"
          control={control}
          rules={{
            validate: (value: string | string[]): string | boolean => {
              if (Array.isArray(value)) {
                return (
                  value.length > 0 ||
                  t(
                    'dashboard_variable_at_least_one_value_required',
                    'At least one value is required'
                  )
                );
              }

              if (variableType === 'multi-value') {
                const values = value
                  .split(',')
                  .map((item: string) => item.trim())
                  .filter((item: string) => item.length > 0);

                return (
                  values.length > 0 ||
                  t(
                    'dashboard_variable_at_least_one_value_required',
                    'At least one value is required'
                  )
                );
              }

              return (
                value.trim().length > 0 ||
                t('dashboard_variable_value_required', 'Value is required')
              );
            },
          }}
          render={({ field, fieldState }: FieldValues): JSX.Element => {
            if (hasVariableSource && variableType === 'multi-value') {
              const selectedOptions = sourceOptions.filter((option: VariableSourceOption) =>
                Array.isArray(field.value) ? field.value.includes(option.value) : false
              );

              return (
                <Autocomplete
                  multiple
                  size="small"
                  options={sourceOptions}
                  value={selectedOptions}
                  loading={sourceOptionsQuery.isLoading}
                  disabled={props.disabled}
                  getOptionLabel={(option: VariableSourceOption): string => option.name}
                  isOptionEqualToValue={(
                    option: VariableSourceOption,
                    value: VariableSourceOption
                  ): boolean => option.value === value.value}
                  onChange={(_: React.SyntheticEvent, options: VariableSourceOption[]): void => {
                    field.onChange(options.map((option: VariableSourceOption) => option.value));
                  }}
                  renderTags={(
                    value: VariableSourceOption[],
                    getTagProps: AutocompleteRenderGetTagProps
                  ): JSX.Element[] =>
                    value.map((option: VariableSourceOption, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });

                      return <Chip {...tagProps} key={key} label={option.name} size="small" />;
                    })
                  }
                  renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
                    <TextField
                      {...params}
                      label={t('dashboard_variable_value', 'Value')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              );
            }

            if (hasVariableSource) {
              const selectedOption =
                sourceOptions.find(
                  (option: VariableSourceOption) => option.value === field.value
                ) ?? null;

              return (
                <Autocomplete
                  size="small"
                  options={sourceOptions}
                  value={selectedOption}
                  loading={sourceOptionsQuery.isLoading}
                  disabled={props.disabled}
                  getOptionLabel={(option: VariableSourceOption): string => option.name}
                  isOptionEqualToValue={(
                    option: VariableSourceOption,
                    value: VariableSourceOption
                  ): boolean => option.value === value.value}
                  onChange={(
                    _: React.SyntheticEvent,
                    option: VariableSourceOption | null
                  ): void => {
                    field.onChange(option?.value ?? '');
                  }}
                  renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
                    <TextField
                      {...params}
                      label={t('dashboard_variable_value', 'Value')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              );
            }

            return (
              <TextField
                value={Array.isArray(field.value) ? field.value.join(', ') : field.value}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                  field.onChange(event.target.value);
                }}
                label={
                  variableType === 'multi-value'
                    ? t('dashboard_variable_values', 'Values')
                    : t('dashboard_variable_value', 'Value')
                }
                size="small"
                fullWidth
                disabled={props.disabled}
                error={!!fieldState.error}
                helperText={
                  fieldState.error?.message ||
                  (variableType === 'multi-value'
                    ? t('dashboard_variable_values_helper', 'Separate multiple values with commas.')
                    : '')
                }
              />
            );
          }}
        />
      </div>

      <div className="flex justify-end gap-2" data-dashboard-tour="variable-form-actions">
        <Button variant="outlined" onClick={props.onCancel} disabled={props.disabled}>
          {t('cancel', 'Cancel')}
        </Button>
        <Button type="submit" variant="contained" disabled={props.disabled}>
          {props.type === 'create' ? t('create', 'Create') : t('save_btn', 'Save')}
        </Button>
      </div>
    </form>
  );
};
